/**
 * Nice Shot — Compare Clubs.
 *
 * A fully additive enhancement: it edits NO shared theme file. It injects a
 * "Compare" toggle onto every product card, keeps a selection in localStorage
 * (so it survives AJAX filtering / sort / paging / navigation), shows a sticky
 * tray, and links to the server-rendered /pages/compare view.
 *
 * Decoupling contract:
 *  - Reads cards by the existing .ns-card + [data-product-id] contract only.
 *  - Re-injects after every grid swap via a debounced MutationObserver, mirroring
 *    ns-facets.js's "delegate everything, survive innerHTML replacement" approach.
 *  - Injection is idempotent (skips cards already wired), so it is safe to run
 *    repeatedly.
 *
 * Integration: load this file + ns-compare.css globally, and create a /pages/compare
 * page using the "NS — Compare Clubs" section. See README.md.
 */
(function () {
  if (window.__nsCompare) return;
  window.__nsCompare = true;

  var STORE_KEY = 'ns-compare-v1';
  var MAX = 4;
  var root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
  if (root.charAt(root.length - 1) !== '/') root += '/';

  // ---- selection state (localStorage-backed) ----
  function load() {
    try {
      var raw = localStorage.getItem(STORE_KEY);
      var arr = raw ? JSON.parse(raw) : [];
      return Array.isArray(arr) ? arr.slice(0, MAX) : [];
    } catch (e) { return []; }
  }
  function save(items) {
    try { localStorage.setItem(STORE_KEY, JSON.stringify(items)); } catch (e) {}
  }
  var selection = load();

  function has(id) {
    id = String(id);
    return selection.some(function (i) { return String(i.id) === id; });
  }
  function indexOf(id) {
    id = String(id);
    for (var i = 0; i < selection.length; i++) {
      if (String(selection[i].id) === id) return i;
    }
    return -1;
  }

  // ---- read the data we need straight off the card DOM ----
  function readCard(card) {
    var id = card.getAttribute('data-product-id');
    if (!id) return null;
    var link = card.querySelector('.ns-card__link');
    var href = link ? link.getAttribute('href') : '';
    var handle = '';
    var m = href && href.match(/\/products\/([^/?#]+)/);
    if (m) handle = m[1];
    var titleEl = card.querySelector('.ns-card__title');
    var brandEl = card.querySelector('.ns-card__brand');
    var priceEl = card.querySelector('.ns-card__price');
    var imgEl = card.querySelector('.ns-card__img');
    return {
      id: String(id),
      handle: handle,
      url: href || (root + 'products/' + handle),
      title: titleEl ? titleEl.textContent.trim().replace(/\s+/g, ' ') : 'Club',
      brand: brandEl ? brandEl.textContent.trim() : '',
      price: priceEl ? priceEl.textContent.trim() : '',
      image: imgEl ? (imgEl.getAttribute('src') || imgEl.currentSrc || '') : ''
    };
  }

  // ---- inject a compare toggle onto a card (idempotent) ----
  function wireCard(card) {
    if (!card || card.__nsCompareWired) return;
    if (!card.getAttribute('data-product-id')) return;
    // Skip the compact cart-upsell variant — compare only makes sense on grids.
    if (card.classList.contains('ns-card--compact')) return;
    card.__nsCompareWired = true;

    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'ns-cmp-toggle';
    btn.setAttribute('data-ns-cmp-toggle', card.getAttribute('data-product-id'));
    btn.innerHTML =
      '<span class="ns-cmp-toggle__box" aria-hidden="true"></span>' +
      '<span class="ns-cmp-toggle__label">Compare</span>';
    // Insert as a direct child of the article, OUTSIDE the wrapping <a>, so a
    // click never triggers card navigation.
    card.appendChild(btn);
    syncToggle(btn, card.getAttribute('data-product-id'));
  }

  function syncToggle(btn, id) {
    var on = has(id);
    btn.classList.toggle('is-on', on);
    btn.setAttribute('aria-pressed', on ? 'true' : 'false');
    var label = btn.querySelector('.ns-cmp-toggle__label');
    if (label) label.textContent = on ? 'Added' : 'Compare';
  }

  function wireAll() {
    var cards = document.querySelectorAll('.ns-card[data-product-id]');
    for (var i = 0; i < cards.length; i++) wireCard(cards[i]);
    syncAllToggles();
  }

  function syncAllToggles() {
    var toggles = document.querySelectorAll('[data-ns-cmp-toggle]');
    for (var i = 0; i < toggles.length; i++) {
      syncToggle(toggles[i], toggles[i].getAttribute('data-ns-cmp-toggle'));
    }
  }

  // ---- toggle click (delegated) ----
  document.addEventListener('click', function (e) {
    var btn = e.target.closest ? e.target.closest('[data-ns-cmp-toggle]') : null;
    if (!btn) return;
    e.preventDefault();
    e.stopPropagation();
    var id = btn.getAttribute('data-ns-cmp-toggle');
    var card = btn.closest('.ns-card');
    if (has(id)) {
      selection.splice(indexOf(id), 1);
    } else {
      if (selection.length >= MAX) { bumpTray(); return; }
      var data = card ? readCard(card) : null;
      if (!data) return;
      selection.push(data);
    }
    save(selection);
    syncAllToggles();
    renderTray();
  });

  // ---- remove chip / clear (delegated) ----
  document.addEventListener('click', function (e) {
    var t = e.target;
    var rm = t.closest ? t.closest('[data-ns-cmp-remove]') : null;
    if (rm) {
      e.preventDefault();
      var i = indexOf(rm.getAttribute('data-ns-cmp-remove'));
      if (i > -1) selection.splice(i, 1);
      save(selection);
      syncAllToggles();
      renderTray();
      return;
    }
    if (t.closest && t.closest('[data-ns-cmp-clear]')) {
      e.preventDefault();
      selection = [];
      save(selection);
      syncAllToggles();
      renderTray();
    }
  });

  // ---- sticky tray ----
  var tray;
  function ensureTray() {
    if (tray) return tray;
    tray = document.createElement('div');
    tray.className = 'ns-cmp-tray';
    tray.setAttribute('data-ns-cmp-tray', '');
    tray.setAttribute('role', 'region');
    tray.setAttribute('aria-label', 'Clubs to compare');
    tray.hidden = true;
    document.body.appendChild(tray);
    return tray;
  }

  function bumpTray() {
    var el = ensureTray();
    el.classList.remove('is-bump');
    // reflow to restart the animation
    void el.offsetWidth;
    el.classList.add('is-bump');
  }

  function renderTray() {
    var el = ensureTray();
    if (!selection.length) {
      el.hidden = true;
      el.innerHTML = '';
      document.body.classList.remove('ns-cmp-has-tray');
      return;
    }
    document.body.classList.add('ns-cmp-has-tray');
    var ids = selection.map(function (i) { return i.handle; }).filter(Boolean).join(',');
    var url = root + 'pages/compare?ids=' + encodeURIComponent(ids);

    var chips = selection.map(function (i) {
      var img = i.image
        ? '<img src="' + i.image + '" alt="" class="ns-cmp-chip__img">'
        : '<span class="ns-cmp-chip__img ns-cmp-chip__img--ph"></span>';
      return (
        '<div class="ns-cmp-chip">' +
          img +
          '<span class="ns-cmp-chip__title">' + escapeHtml(i.title) + '</span>' +
          '<button type="button" class="ns-cmp-chip__x" data-ns-cmp-remove="' + i.id + '" ' +
            'aria-label="Remove ' + escapeHtml(i.title) + ' from compare">&times;</button>' +
        '</div>'
      );
    }).join('');

    // pad empty slots so the tray reads as "you can add up to 4"
    var empties = '';
    for (var e = selection.length; e < MAX; e++) {
      empties += '<div class="ns-cmp-chip ns-cmp-chip--empty" aria-hidden="true"><span class="ns-cmp-chip__slot">+</span></div>';
    }

    el.innerHTML =
      '<div class="ns-cmp-tray__inner">' +
        '<div class="ns-cmp-tray__lead">' +
          '<span class="ns-cmp-tray__count">Compare <strong>' + selection.length + '</strong>/' + MAX + '</span>' +
          '<button type="button" class="ns-cmp-tray__clear" data-ns-cmp-clear>Clear</button>' +
        '</div>' +
        '<div class="ns-cmp-tray__chips">' + chips + empties + '</div>' +
        '<a href="' + url + '" class="ns-cmp-tray__go"' + (selection.length < 2 ? ' aria-disabled="true"' : '') + '>' +
          'Compare' + (selection.length >= 2 ? ' clubs' : '') +
          '<span class="ns-cmp-tray__go-hint">' + (selection.length < 2 ? 'Add one more' : '') + '</span>' +
        '</a>' +
      '</div>';
    el.hidden = false;
  }

  // The "Compare" CTA opens a modal over the current page (keeps the shopper in flow) instead
  // of navigating to /pages/compare. That page still works as a fallback for direct links.
  document.addEventListener('click', function (e) {
    var go = e.target.closest ? e.target.closest('.ns-cmp-tray__go') : null;
    if (!go) return;
    if (go.getAttribute('aria-disabled') === 'true') { e.preventDefault(); bumpTray(); return; }
    e.preventDefault();
    openCompareModal();
  });

  // ---- compare modal (overlay) ----
  function openCompareModal() {
    var ov = document.querySelector('[data-ns-cmp-modal]');
    if (!ov) {
      ov = document.createElement('div');
      ov.className = 'ns-cmp-modal';
      ov.setAttribute('data-ns-cmp-modal', '');
      ov.setAttribute('role', 'dialog');
      ov.setAttribute('aria-modal', 'true');
      ov.setAttribute('aria-label', 'Compare clubs');
      ov.hidden = true;
      ov.innerHTML =
        '<div class="ns-cmp-modal__panel">' +
          '<div class="ns-cmp-modal__head">' +
            '<div><p class="ns-eyebrow">Side by side</p><h2 class="ns-cmp-modal__title">Compare clubs</h2></div>' +
            '<button type="button" class="ns-cmp-modal__close" data-ns-cmp-modal-close aria-label="Close compare">&times;</button>' +
          '</div>' +
          '<div class="ns-cmp-modal__body" data-ns-cmp-mount data-base-url="' + root + '"></div>' +
        '</div>';
      document.body.appendChild(ov);
      ov.addEventListener('click', function (ev) {
        if (ev.target === ov || (ev.target.closest && ev.target.closest('[data-ns-cmp-modal-close]'))) closeCompareModal();
      });
    }
    var mount = ov.querySelector('[data-ns-cmp-mount]');
    mount.innerHTML = '<div class="ns-cmp-page__loading">Loading your clubs…</div>';
    ov.hidden = false;
    document.body.classList.add('ns-cmp-modal-open');
    renderComparePage(mount);
  }
  function closeCompareModal() {
    var ov = document.querySelector('[data-ns-cmp-modal]');
    if (ov) ov.hidden = true;
    document.body.classList.remove('ns-cmp-modal-open');
  }
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') closeCompareModal();
  });

  function escapeHtml(s) {
    return String(s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  // ---- re-inject after AJAX grid swaps ----
  var reflowTimer = null;
  function scheduleWire() {
    clearTimeout(reflowTimer);
    reflowTimer = setTimeout(wireAll, 80);
  }
  var mo = new MutationObserver(function (muts) {
    for (var i = 0; i < muts.length; i++) {
      if (muts[i].addedNodes && muts[i].addedNodes.length) { scheduleWire(); return; }
    }
  });

  // ================================================================
  // Compare page renderer (only runs where [data-ns-cmp-mount] exists)
  // ================================================================
  var FIELDS = [
    { key: 'condition', label: 'Condition' },
    { key: 'loft', label: 'Loft' },
    { key: 'shaft', label: 'Shaft' },
    { key: 'flex', label: 'Flex' },
    { key: 'hand', label: 'Hand' },
    { key: 'clubs', label: 'Clubs in set' },
    { key: 'subtype', label: 'Head type' },
    { key: 'headcc', label: 'Head volume' },
    { key: 'adjustable', label: 'Adjustable hosel' },
    { key: 'year', label: 'Model year' },
    { key: 'whofor', label: "Who it's for" },
    { key: 'handicap', label: 'Handicap range' },
    { key: 'swing', label: 'Swing speed' }
  ];

  function addToCart(btn) {
    var id = btn.getAttribute('data-ns-cmp-add');
    if (!id || btn.disabled) return;
    var original = btn.textContent;
    btn.disabled = true;
    btn.textContent = 'Adding…';

    fetch(window.Shopify && window.Shopify.routes ? window.Shopify.routes.root + 'cart/add.js' : '/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: Number(id), quantity: 1 }] })
    })
      .then(function (r) { if (!r.ok) throw new Error('add failed'); return r.json(); })
      .then(function () {
        return fetch(window.Shopify && window.Shopify.routes ? window.Shopify.routes.root + 'cart.js' : '/cart.js', {
          headers: { Accept: 'application/json' }
        }).then(function (r) { return r.json(); });
      })
      .then(function (cart) {
        // Same shape cart-icon.js expects; source is not 'product-form-component',
        // so it SETS the count rather than incrementing it.
        document.dispatchEvent(
          new CustomEvent('cart:update', {
            bubbles: true,
            detail: { data: { itemCount: cart.item_count, source: 'ns-compare' } }
          })
        );
        btn.textContent = 'Added';
        btn.classList.add('is-added');
        setTimeout(function () {
          btn.textContent = original;
          btn.classList.remove('is-added');
          btn.disabled = false;
        }, 2200);
      })
      .catch(function () {
        btn.textContent = 'Try again';
        btn.disabled = false;
      });
  }

  document.addEventListener('click', function (e) {
    var btn = e.target.closest && e.target.closest('[data-ns-cmp-add]');
    if (btn) { e.preventDefault(); addToCart(btn); }
  });

  function handlesFromUrl() {
    try {
      var ids = new URLSearchParams(window.location.search).get('ids');
      return ids ? ids.split(',').map(function (h) { return h.trim(); }).filter(Boolean) : [];
    } catch (e) { return []; }
  }

  function fetchCell(base, handle) {
    var url = base + 'products/' + encodeURIComponent(handle) + '?section_id=ns-compare-cell';
    return fetch(url)
      .then(function (r) { if (!r.ok) throw new Error('HTTP ' + r.status); return r.text(); })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var blob = doc.querySelector('script[data-ns-cmp-data]');
        if (!blob) return null;
        try { return JSON.parse(blob.textContent); } catch (e) { return null; }
      })
      .catch(function () { return null; });
  }

  function renderComparePage(mount) {
    var base = mount.getAttribute('data-base-url') || root;
    if (base.charAt(base.length - 1) !== '/') base += '/';
    var handles = handlesFromUrl();
    if (!handles.length) {
      handles = selection.map(function (i) { return i.handle; }).filter(Boolean);
    }
    handles = handles.slice(0, MAX);

    if (!handles.length) {
      mount.innerHTML =
        '<div class="ns-cmp-empty">Nothing to compare yet. Pick a couple of clubs from the range and hit “Compare”.' +
        '<br><a class="ns-cmp-empty__cta" href="' + base + 'collections/all">Browse clubs</a></div>';
      return;
    }

    Promise.all(handles.map(function (h) { return fetchCell(base, h); }))
      .then(function (results) {
        var products = results.filter(Boolean);
        if (!products.length) {
          mount.innerHTML = '<div class="ns-cmp-empty">We couldn’t load those clubs. They may have sold. ' +
            '<a class="ns-cmp-empty__cta" href="' + base + 'collections/all">Browse clubs</a></div>';
          return;
        }
        buildGrid(mount, products);
      });
  }

  function cell(cls, html) {
    return '<div class="ns-cmp-grid__cell ' + cls + '">' + html + '</div>';
  }

  function buildGrid(mount, products) {
    var n = products.length;
    var cols = 'minmax(140px, 168px) repeat(' + n + ', minmax(180px, 1fr))';

    // header row
    var head = '<div class="ns-cmp-grid__row">';
    head += '<div class="ns-cmp-head ns-cmp-head--label"></div>';
    // Strip a leading brand off the title so it doesn't repeat the brand line above it
    // (the product card does the same via club_model.display_name).
    function modelTitle(p) {
      var t = String(p.title || '');
      var b = String(p.brand || '');
      if (b && t.toLowerCase().indexOf(b.toLowerCase()) === 0) {
        var stripped = t.slice(b.length).replace(/^[\s\-–|]+/, '');
        if (stripped) return stripped;
      }
      return t;
    }

    products.forEach(function (p) {
      var img = p.image
        ? '<img class="ns-cmp-head__img" src="' + p.image + '" alt="' + escapeHtml(p.title) + '">'
        : '<span class="ns-cmp-head__img"></span>';
      var rrp = p.compare_at ? '<span class="ns-cmp-head__rrp">' + escapeHtml(p.compare_at) + '</span>' : '';
      head +=
        '<div class="ns-cmp-head">' +
          '<a href="' + p.url + '">' + img + '</a>' +
          (p.brand ? '<p class="ns-cmp-head__brand">' + escapeHtml(p.brand) + '</p>' : '') +
          '<h2 class="ns-cmp-head__title">' + escapeHtml(modelTitle(p)) + '</h2>' +
          '<p class="ns-cmp-head__price">' + escapeHtml(p.price || '') + rrp + '</p>' +
          (p.available && p.variant_id
            ? '<button type="button" class="ns-cmp-head__atc" data-ns-cmp-add="' + p.variant_id + '">Add to cart</button>'
            : '<span class="ns-cmp-head__sold">Sold</span>') +
          '<div class="ns-cmp-head__actions">' +
            '<a class="ns-cmp-head__view" href="' + p.url + '">View</a>' +
            '<button type="button" class="ns-cmp-head__remove" data-ns-cmp-page-remove="' + escapeHtml(p.handle) + '">Remove</button>' +
          '</div>' +
        '</div>';
    });
    head += '</div>';

    // spec rows — drop a row entirely if every product is blank for it
    var body = '';
    var rowIdx = 0;
    FIELDS.forEach(function (f) {
      var vals = products.map(function (p) { return (p.fields && p.fields[f.key]) || ''; });
      var anyVal = vals.some(function (v) { return v !== '' && v != null; });
      if (!anyVal) return;
      var alt = (rowIdx % 2 === 1) ? ' is-alt' : '';
      rowIdx++;
      var row = '<div class="ns-cmp-grid__row' + alt + '">';
      row += '<div class="ns-cmp-grid__cell ns-cmp-grid__label">' + escapeHtml(f.label) + '</div>';
      vals.forEach(function (v) {
        row += (v === '' || v == null)
          ? cell('ns-cmp-val--empty', '–')
          : cell('', escapeHtml(String(v)));
      });
      row += '</div>';
      body += row;
    });

    mount.innerHTML =
      '<div class="ns-cmp-grid" style="grid-template-columns:' + cols + '">' + head + body + '</div>';
  }

  // remove a column from the compare page
  document.addEventListener('click', function (e) {
    var rm = e.target.closest ? e.target.closest('[data-ns-cmp-page-remove]') : null;
    if (!rm) return;
    e.preventDefault();
    var handle = rm.getAttribute('data-ns-cmp-page-remove');
    // drop from the saved selection too, if present
    for (var i = selection.length - 1; i >= 0; i--) {
      if (selection[i].handle === handle) selection.splice(i, 1);
    }
    save(selection);
    // update the URL + re-render
    var remaining = handlesFromUrl().filter(function (h) { return h !== handle; });
    if (!remaining.length) {
      remaining = selection.map(function (s) { return s.handle; }).filter(Boolean);
    }
    try {
      var u = new URL(window.location.href);
      if (remaining.length) u.searchParams.set('ids', remaining.join(','));
      else u.searchParams.delete('ids');
      history.replaceState({}, '', u.toString());
    } catch (err) {}
    var mount = document.querySelector('[data-ns-cmp-mount]');
    if (mount) renderComparePage(mount);
    renderTray();
  });

  function init() {
    wireAll();
    renderTray();
    var mount = document.querySelector('[data-ns-cmp-mount]');
    if (mount) renderComparePage(mount);
    var target = document.querySelector('[data-ns-facets-root]') || document.body;
    mo.observe(target, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
